/**
 * Wrapper do fetch com renovação automática de token
 *
 * Esta função intercepta erros 401 e tenta renovar o token automaticamente
 * até 3 vezes antes de redirecionar para o login.
 */

import { API_BASE_URL } from '../services/api';
import { logger } from './logger';

// Salvar referência ao fetch original antes de qualquer sobrescrita
const originalFetch = window.fetch.bind(window);

interface RefreshResult {
  accessToken: string;
  refreshToken: string;
  user?: { id: string; email: string; name: string; role: string; tenantId: string | null };
}

let isRefreshing = false;
let refreshPromise: Promise<RefreshResult> | null = null;

/**
 * Renova o access token usando o refresh token
 */
async function refreshAccessToken(): Promise<RefreshResult> {
  const refreshToken = localStorage.getItem('refreshToken');

  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  const response = await originalFetch(`${API_BASE_URL}/api/auth/refresh`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ refreshToken }),
  });

  if (!response.ok) {
    throw new Error('Failed to refresh token');
  }

  const data = await response.json();

  if (!data.success || !data.data) {
    throw new Error('Invalid refresh response');
  }

  const { accessToken, refreshToken: newRefreshToken, user } = data.data;

  // Atualizar tokens no localStorage
  localStorage.setItem('accessToken', accessToken);
  localStorage.setItem('refreshToken', newRefreshToken);

  // Atualizar user no localStorage se veio dados frescos do backend
  if (user) {
    localStorage.setItem('user', JSON.stringify(user));
  }

  logger.log('[fetchWithAuth] Token renovado com sucesso');

  return { accessToken, refreshToken: newRefreshToken, user };
}

/**
 * Executa uma requisição com renovação automática de token em caso de 401
 */
export async function fetchWithAuth(
  url: string | URL | Request,
  options: RequestInit = {},
  retryCount = 0,
  maxRetries = 3
): Promise<Response> {
  // Se não for uma string, converter para string para poder verificar
  const urlString = typeof url === 'string' ? url : url instanceof URL ? url.toString() : '';

  // Pegar o token atual
  const currentToken = localStorage.getItem('accessToken');

  // Adicionar o token de autorização se houver e se não for uma requisição de auth
  if (currentToken && !urlString.includes('/auth/')) {
    options.headers = {
      ...options.headers,
      'Authorization': `Bearer ${currentToken}`,
    };
  }

  try {
    const response = await originalFetch(url, options);

    // Se não for 401, retornar a resposta normalmente
    if (response.status !== 401) {
      return response;
    }

    // Se for 401 e já tentamos o máximo de vezes, limpar tudo e redirecionar
    if (retryCount >= maxRetries) {
      logger.log('[fetchWithAuth] Máximo de tentativas atingido - redirecionando para login');
      localStorage.removeItem('user');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      window.location.href = '/login';
      throw new Error('Sessão expirada. Por favor, faça login novamente.');
    }

    // Se for 401 e for uma requisição de auth, não tentar refresh
    if (urlString.includes('/auth/')) {
      return response;
    }

    logger.log(`[fetchWithAuth] 401 detectado - Tentativa ${retryCount + 1} de ${maxRetries}`);

    // Se já estamos renovando, aguardar a promessa existente
    if (isRefreshing && refreshPromise) {
      logger.log('[fetchWithAuth] Aguardando renovação em andamento...');
      try {
        const { accessToken } = await refreshPromise;

        // Atualizar o header com o novo token e tentar novamente
        const newOptions = {
          ...options,
          headers: {
            ...options.headers,
            'Authorization': `Bearer ${accessToken}`,
          },
        };

        return fetchWithAuth(url, newOptions, retryCount + 1, maxRetries);
      } catch (error) {
        // Se a renovação falhou, limpar tudo
        localStorage.removeItem('user');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        throw error;
      }
    }

    // Iniciar renovação
    isRefreshing = true;
    refreshPromise = refreshAccessToken();

    try {
      const { accessToken } = await refreshPromise;

      // Atualizar o header com o novo token e tentar novamente
      const newOptions = {
        ...options,
        headers: {
          ...options.headers,
          'Authorization': `Bearer ${accessToken}`,
        },
      };

      return await fetchWithAuth(url, newOptions, retryCount + 1, maxRetries);
    } catch (refreshError) {
      logger.error('[fetchWithAuth] Erro ao renovar token:', refreshError);

      // Se ainda temos tentativas, aguardar um pouco e tentar novamente
      if (retryCount < maxRetries - 1) {
        logger.log(`[fetchWithAuth] Aguardando 500ms antes da próxima tentativa...`);
        await new Promise(resolve => setTimeout(resolve, 500));
        return fetchWithAuth(url, options, retryCount + 1, maxRetries);
      }

      // Limpar tudo e redirecionar
      localStorage.removeItem('user');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      window.location.href = '/login';
      throw new Error('Sessão expirada. Por favor, faça login novamente.');
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  } catch (error) {
    // Se for um erro de rede ou outro erro que não seja 401, propagar
    if (error instanceof Error && error.message !== 'Sessão expirada. Por favor, faça login novamente.') {
      throw error;
    }
    throw error;
  }
}
